import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { embedDashboard } from '@superset-ui/embedded-sdk'
import { common, errors, layouts } from '@ocean/ui'
import { useLazySelector } from 'hooks'
import { ErrorWrapperLayout } from 'layouts'
import { Skeleton } from 'components/common'
import { SkeletonVariant } from 'components/common/Skeleton'
import { getDashboardToken } from './api'
import { namespace } from './localization'

const { ErrorResult } = errors
const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const Dashboard: React.FC = () => {
  const supersetContainerRef = useRef<HTMLDivElement>(null)
  const [tokenError, setTokenError] = useState<string>()
  const { t } = useTranslation(namespace)

  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation

    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })

  useEffect(() => {
    const fetchGuestToken = async () => {
      const { content, error } = await getDashboardToken()

      if (error || !content.token) setTokenError(error?.error ?? t('failedToAuthenticate'))

      return content.token
    }

    if (supersetContainerRef.current && !isLoading) {
      embedDashboard({
        id: window.cc.dashboardId,
        supersetDomain: window.cc.supersetDashboard,
        mountPoint: supersetContainerRef.current,
        fetchGuestToken,
        iframeAllowExtras: ['fullscreen'],
        dashboardUiConfig: {
          hideTitle: true,
          hideChartControls: false,
          hideTab: true,
        },
      })
      supersetContainerRef.current.firstElementChild?.setAttribute('height', '100%')
      supersetContainerRef.current.firstElementChild?.setAttribute('width', '100%')

      //? this is to hide track bars from the window
      const trackVerticalElements = document.querySelectorAll<HTMLElement>(
        '[data-selector="TrackVertical"]'
      )

      if (trackVerticalElements.length >= 2) {
        for (let i = trackVerticalElements.length - 1; i >= trackVerticalElements.length - 2; i--) {
          trackVerticalElements[i].style.visibility = 'hidden'
        }
      }
      const parentDiv = supersetContainerRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }, [customerId, supersetContainerRef, isLoading, t])

  if (isLoading) return <Skeleton variant={SkeletonVariant.Details} />

  if (error || tokenError)
    return (
      <ErrorWrapperLayout>
        <ErrorResult error={tokenError ?? error.error} dataTest='ProgramDetailsError' />
      </ErrorWrapperLayout>
    )

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <div
            ref={supersetContainerRef}
            style={{ height: '100%', width: '100%', overflow: 'hidden' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default Dashboard
